import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '@env/environment';
import { Observable, of } from 'rxjs';
import { catchError, finalize, map, shareReplay, tap } from 'rxjs/operators';

export type AppScreenAction = 'view' | 'create' | 'edit' | 'delete' | 'export';

export interface AppScreenPermission {
    id?: string;
    screenId: string;
    screenCode: string;
    menuId: string;
    title: string;
    route: string;
    canView: boolean;
    canCreate: boolean;
    canEdit: boolean;
    canDelete: boolean;
    canExport: boolean;
}

export interface CurrentUserPermissions {
    isSuperAdmin: boolean;
    roles: string[];
    screens: AppScreenPermission[];
}

@Injectable({ providedIn: 'root' })
export class AppPermissionService {
    private currentPermissions: CurrentUserPermissions | null = null;
    private loadRequest$: Observable<CurrentUserPermissions | null> | null = null;
    private loadVersion = 0;

    constructor(private http: HttpClient) { }

    loadCurrent(force = false): Observable<CurrentUserPermissions | null> {
        if (!force && this.currentPermissions) {
            return of(this.currentPermissions);
        }

        if (!force && this.loadRequest$) {
            return this.loadRequest$;
        }

        if (force) {
            this.loadVersion++;
        }

        const requestVersion = this.loadVersion;
        const request$ = this.http.get<CurrentUserPermissions>(`${environment.apiv2}/permissions/current`).pipe(
            tap(response => {
                if (requestVersion === this.loadVersion) {
                    this.currentPermissions = response;
                }
            }),
            catchError(() => {
                if (requestVersion === this.loadVersion) {
                    this.currentPermissions = null;
                }
                return of(null);
            }),
            finalize(() => {
                if (this.loadRequest$ === request$) {
                    this.loadRequest$ = null;
                }
            }),
            shareReplay(1)
        );

        this.loadRequest$ = request$;

        return this.loadRequest$;
    }

    clear(): void {
        this.loadVersion++;
        this.currentPermissions = null;
        this.loadRequest$ = null;
    }

    canViewSync(screenCodeOrMenuId: string): boolean | null {
        return this.canSync(screenCodeOrMenuId, 'view');
    }

    isSuperAdminSync(): boolean {
        return this.currentPermissions?.isSuperAdmin === true;
    }

    canSync(screenCodeOrMenuId: string, action: AppScreenAction): boolean | null {
        if (!screenCodeOrMenuId) {
            return true;
        }

        if (!this.currentPermissions) {
            return null;
        }

        if (this.currentPermissions.isSuperAdmin) {
            return true;
        }

        const target = screenCodeOrMenuId.toLocaleLowerCase();
        const screen = this.currentPermissions.screens?.find(item =>
            item.screenCode?.toLocaleLowerCase() === target || item.menuId?.toLocaleLowerCase() === target);

        if (!screen) {
            return null;
        }

        switch (action) {
            case 'view':
                return screen.canView;
            case 'create':
                return screen.canCreate;
            case 'edit':
                return screen.canEdit;
            case 'delete':
                return screen.canDelete;
            case 'export':
                return screen.canExport;
            default:
                return false;
        }
    }

    can(screenCodeOrMenuId: string, action: AppScreenAction = 'view'): Observable<boolean> {
        return this.loadCurrent().pipe(
            map(() => this.canSync(screenCodeOrMenuId, action) === true)
        );
    }
}
